import { ArrowRight, Plus, RefreshCw, Share2, UsersRound } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type { LoopAttribute } from "../../shared/tags";
import { ApiError, api, formatRelative } from "../api";
import { Button, LoadingState, ProviderMark } from "../components";
import {
  type RuleShape,
  type RuleSuggestionSource,
  type SharingGroup,
  type SharingLoop,
  type SharingRule,
  matchCount,
  ownRuleShares,
  ruleForSharedLoop,
  ruleSentence,
  ruleSuggestions,
  sharedByLine,
} from "./sharingBulk";
import { useAuth } from "../auth";
import "../styles/administration.css";

/**
 * Standing sharing rules for a personal workspace, and the group's read-only view of what rules and
 * people shared into it. The routes mirror src/worker/sharingRules.ts; a 404 from any of them renders
 * the empty state so the page works before the worker learns the routes.
 */

export type SharingRulesState = {
  rules: SharingRule[];
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
};

function isNotFound(caught: unknown): boolean {
  return caught instanceof ApiError && caught.status === 404;
}

export function useSharingRules(organizationId: string, enabled: boolean): SharingRulesState {
  const [rules, setRules] = useState<SharingRule[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!enabled || !organizationId) {
      setRules([]);
      setLoading(false);
      return;
    }
    try {
      const result = await api<{ rules?: SharingRule[] } | SharingRule[]>("/api/app/sharing/rules", {}, organizationId);
      setRules(Array.isArray(result) ? result : result.rules ?? []);
      setError("");
    } catch (caught) {
      setRules([]);
      setError(isNotFound(caught) ? "" : caught instanceof Error ? caught.message : "Sharing rules could not be read");
    } finally {
      setLoading(false);
    }
  }, [enabled, organizationId]);

  useEffect(() => {
    setLoading(enabled);
    void refresh();
  }, [enabled, refresh]);

  return { rules, loading, error, refresh };
}

export function useRuleSuggestions(organizationId: string, enabled: boolean): RuleSuggestionSource | null {
  const [suggestions, setSuggestions] = useState<RuleSuggestionSource | null>(null);
  useEffect(() => {
    if (!enabled || !organizationId) return;
    let cancelled = false;
    api<RuleSuggestionSource>("/api/app/sharing/suggestions", {}, organizationId)
      .then((result) => { if (!cancelled) setSuggestions(result); })
      .catch(() => { if (!cancelled) setSuggestions(null); });
    return () => { cancelled = true; };
  }, [enabled, organizationId]);
  return suggestions;
}

/**
 * The rules behind the shares the viewer made from their personal workspace, keyed "loopId:groupId".
 * Group views use it to label the viewer's own rule-made shares; both reads tolerate a 404.
 */
export function useOwnRuleShares(personalOrganizationId: string | null | undefined, enabled: boolean): ReadonlyMap<string, RuleShape> {
  const [shares, setShares] = useState<ReadonlyMap<string, RuleShape>>(new Map());
  useEffect(() => {
    if (!enabled || !personalOrganizationId) return;
    let cancelled = false;
    Promise.all([
      api<{ loops: SharingLoop[] }>("/api/app/sharing", {}, personalOrganizationId).catch(() => ({ loops: [] as SharingLoop[] })),
      api<{ rules?: SharingRule[] } | SharingRule[]>("/api/app/sharing/rules", {}, personalOrganizationId).catch(() => ({ rules: [] as SharingRule[] })),
    ]).then(([overview, rules]) => {
      if (!cancelled) setShares(ownRuleShares(overview.loops, Array.isArray(rules) ? rules : rules.rules ?? []));
    });
    return () => { cancelled = true; };
  }, [enabled, personalOrganizationId]);
  return shares;
}

export async function createRule(organizationId: string, input: { targetOrganizationId: string; attribute: LoopAttribute; value: string }): Promise<void> {
  await api("/api/app/sharing/rules", { method: "POST", body: JSON.stringify(input) }, organizationId);
}

export async function revokeRule(organizationId: string, ruleId: string, withdraw: boolean): Promise<void> {
  await api(`/api/app/sharing/rules/${encodeURIComponent(ruleId)}`, { method: "DELETE", body: JSON.stringify({ withdraw }) }, organizationId);
}

/** The message a person reads when a rule action fails, with the two expected refusals named plainly. */
export function ruleErrorMessage(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError && caught.status === 409) return "That rule already exists.";
  if (isNotFound(caught)) return "Sharing rules are not available yet.";
  return caught instanceof Error ? caught.message : fallback;
}

const ATTRIBUTE_OPTIONS: Array<{ value: LoopAttribute; label: string; placeholder: string }> = [
  { value: "tag", label: "Tag", placeholder: "peptide-app" },
  { value: "source", label: "Source", placeholder: "Choose a source" },
  { value: "kind", label: "Kind", placeholder: "automation" },
  { value: "cadence", label: "Cadence", placeholder: "daily" },
];

export function SharingRulesPanel({ organizationId, groups, loops, canManage, state, suggestions, onChanged }: {
  organizationId: string;
  groups: SharingGroup[];
  loops: SharingLoop[];
  canManage: boolean;
  state: SharingRulesState;
  suggestions: RuleSuggestionSource | null;
  onChanged: () => Promise<void> | void;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [attribute, setAttribute] = useState<LoopAttribute>("tag");
  const [value, setValue] = useState("");
  const [targetId, setTargetId] = useState(groups[0]?.id ?? "");
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [busyRule, setBusyRule] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!targetId || !groups.some((group) => group.id === targetId)) setTargetId(groups[0]?.id ?? "");
  }, [groups, targetId]);

  const options = useMemo(() => ruleSuggestions(loops, suggestions), [loops, suggestions]);
  const attributeOption = ATTRIBUTE_OPTIONS.find((option) => option.value === attribute)!;
  const matches = matchCount(loops, attribute, value);
  const target = groups.find((group) => group.id === targetId);
  const sourceName = attribute === "source" ? options.source.find((option) => option.value === value)?.label ?? null : null;
  const preview: RuleShape | null = value.trim() ? { attribute, value: value.trim(), sourceName } : null;

  function chooseAttribute(next: LoopAttribute) {
    setAttribute(next);
    setValue(next === "source" ? options.source[0]?.value ?? "" : "");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!preview || !targetId) return;
    setCreating(true);
    setError("");
    setNotice("");
    try {
      await createRule(organizationId, { targetOrganizationId: targetId, attribute, value: preview.value });
      setNotice(`Rule created: ${ruleSentence(preview, target?.name ?? "the group")}.`);
      setFormOpen(false);
      setValue("");
      await state.refresh();
      await onChanged();
    } catch (caught) {
      setError(ruleErrorMessage(caught, "The rule could not be created"));
    } finally {
      setCreating(false);
    }
  }

  async function revoke(rule: SharingRule, withdraw: boolean) {
    setBusyRule(rule.id);
    setError("");
    setNotice("");
    try {
      await revokeRule(organizationId, rule.id, withdraw);
      setNotice(withdraw ? `Rule revoked · ${rule.sharedCount} ${rule.sharedCount === 1 ? "share" : "shares"} withdrawn from ${rule.targetName}.` : `Rule revoked · the loops stay shared with ${rule.targetName}.`);
      setRevoking(null);
      await state.refresh();
      await onChanged();
    } catch (caught) {
      setError(ruleErrorMessage(caught, "The rule could not be revoked"));
    } finally {
      setBusyRule("");
    }
  }

  return (
    <section className="panel admin-surface sharing-rules" id="sharing-rules" aria-labelledby="sharing-rules-title">
      <header className="admin-surface__head">
        <div><h2 className="eyebrow" id="sharing-rules-title">Sharing rules</h2><p>A rule shares every loop that matches one attribute with one group, now and whenever the catalog syncs. Revoking a rule never touches a loop you shared by hand.</p></div>
        {canManage && groups.length > 0 && <Button variant="secondary" onClick={() => { setFormOpen((open) => !open); setError(""); }} aria-expanded={formOpen}><Plus size={15} aria-hidden="true" /> New rule</Button>}
      </header>

      {formOpen && canManage && (
        <form className="sharing-rule-form" onSubmit={(event) => void submit(event)} aria-label="New rule">
          <div className="sharing-rule-form__row">
            <fieldset className="sharing-rule-form__attributes">
              <legend>Share by</legend>
              {ATTRIBUTE_OPTIONS.map((option) => <button type="button" key={option.value} className={attribute === option.value ? "is-selected" : ""} aria-pressed={attribute === option.value} onClick={() => chooseAttribute(option.value)}>{option.label}</button>)}
            </fieldset>
            <label className="sharing-rule-form__value">
              <span>{attributeOption.label}</span>
              {attribute === "source" ? (
                <select value={value} onChange={(event) => setValue(event.target.value)} required>
                  {options.source.length === 0 && <option value="">No sources reported yet</option>}
                  {options.source.map((option) => <option key={option.value} value={option.value}>{option.label} · {option.count}</option>)}
                </select>
              ) : (
                <input value={value} onChange={(event) => setValue(event.target.value)} placeholder={attributeOption.placeholder} required minLength={1} maxLength={60} />
              )}
            </label>
            <label className="sharing-rule-form__target">
              <span>Shared with</span>
              <select value={targetId} onChange={(event) => setTargetId(event.target.value)} required>
                {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
              </select>
            </label>
          </div>
          {attribute !== "source" && options[attribute].length > 0 && (
            <div className="sharing-rule-form__suggestions" aria-label={`${attributeOption.label} suggestions`}>
              {options[attribute].slice(0, 10).map((option) => <button type="button" key={option.value} className={`sharing-chip ${value.trim().toLowerCase() === option.value.toLowerCase() ? "is-selected" : ""}`} aria-pressed={value.trim().toLowerCase() === option.value.toLowerCase()} onClick={() => setValue(option.value)}>{option.label} <b>{option.count}</b></button>)}
            </div>
          )}
          <div className="sharing-rule-form__foot">
            <span className="sharing-rule-form__preview" aria-live="polite">{preview ? <><strong>{matches} {matches === 1 ? "loop matches" : "loops match"} now.</strong> {ruleSentence(preview, target?.name ?? "the group")}.</> : "Pick a value to see how many loops match."}</span>
            <div><Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Button><Button type="submit" busy={creating} disabled={!preview || !targetId}>Create rule</Button></div>
          </div>
        </form>
      )}

      {state.loading ? (
        <LoadingState>Reading sharing rules…</LoadingState>
      ) : state.error ? (
        <div className="state-message state-message--error" role="alert"><RefreshCw size={18} aria-hidden="true" /><p>{state.error}</p><Button variant="secondary" onClick={() => void state.refresh()}>Try again</Button></div>
      ) : state.rules.length ? (
        <div className="sharing-rule-list">
          <div className="sharing-rule-list__head" aria-hidden="true"><span>Rule</span><span>Shared with</span><span>Matches</span><span>Shared</span><span>Created</span><span /></div>
          {state.rules.map((rule) => (
            <article key={rule.id} className={revoking === rule.id ? "is-revoking" : ""}>
              <strong className="sharing-rule__sentence">{ruleSentence(rule, rule.targetName)}</strong>
              <span className="sharing-rule__target"><UsersRound size={12} aria-hidden="true" /> {rule.targetName}</span>
              <span className="sharing-rule__count"><b>{rule.matchCount}</b> match</span>
              <span className="sharing-rule__count"><b>{rule.sharedCount}</b> shared</span>
              <span className="sharing-rule__created">{formatRelative(rule.createdAt)}</span>
              {canManage ? (
                revoking === rule.id ? (
                  <div className="sharing-rule__revoke" role="group" aria-label={`Revoke ${ruleSentence(rule, rule.targetName)}`}>
                    <span>Revoke and…</span>
                    <Button variant="secondary" busy={busyRule === rule.id} onClick={() => void revoke(rule, false)}>Keep the loops shared</Button>
                    <Button variant="danger" busy={busyRule === rule.id} onClick={() => void revoke(rule, true)}>Withdraw the shares</Button>
                    <Button variant="ghost" onClick={() => setRevoking(null)}>Cancel</Button>
                  </div>
                ) : (
                  <Button variant="ghost" className="sharing-rule__revoke-button" onClick={() => { setRevoking(rule.id); setError(""); }}>Revoke</Button>
                )
              ) : <span />}
            </article>
          ))}
        </div>
      ) : (
        <p className="sharing-rules__empty"><b>No rules yet.</b> Share by one tag, source, kind, or cadence and the rule keeps sharing as the catalog grows. Every grant it makes stays visible and reversible.</p>
      )}
      {notice && <p className="sharing-rules__notice" role="status">{notice}</p>}
      {error && <div className="form-error sharing-rules__error" role="alert">{error}</div>}
    </section>
  );
}

type GroupSharedLoop = {
  id: string;
  name: string;
  cadence: string;
  owner: { id: string; name: string } | null;
  discovery: { source: { id: string; provider: string | null; name: string | null } } | null;
  visibility: { mode: "native" | "shared"; originOrganizationName: string; rule?: RuleShape | null; ruleId?: string | null };
};

/** What a group sees on its Sharing page: every loop shared into it, with who shared it and the rule when one did. */
export function GroupSharingView({ organizationId }: { organizationId: string }) {
  const { personalOrganization } = useAuth();
  const [loops, setLoops] = useState<GroupSharedLoop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const ownRules = useOwnRuleShares(personalOrganization?.id, true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api<{ loops: GroupSharedLoop[] }>("/api/app/loops", {}, organizationId)
      .then((result) => { if (!cancelled) { setLoops(result.loops.filter((loop) => loop.visibility.mode === "shared")); setError(""); } })
      .catch((caught: unknown) => { if (!cancelled) setError(isNotFound(caught) ? "" : caught instanceof Error ? caught.message : "Shared loops could not be read"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [organizationId, refreshKey]);

  const ruleFor = (loop: GroupSharedLoop) => ruleForSharedLoop(loop, organizationId, ownRules);
  const byRule = loops.filter((loop) => ruleFor(loop) || loop.visibility.ruleId).length;

  return (
    <div className="sharing-host">
      <section className="sharing-stage sharing-stage--group" aria-labelledby="group-sharing-title">
        <header>
          <div><h2 className="eyebrow" id="group-sharing-title">Shared into this group</h2><p>Every loop below was granted from someone’s private workspace, by hand or by a rule they set. Unsharing happens there, not here.</p></div>
          {!loading && !error && <span className="sharing-stage__lock"><Share2 size={12} aria-hidden="true" /> {loops.length} shared{byRule ? ` · ${byRule} by rule` : ""}</span>}
        </header>
        {loading ? (
          <div className="sharing-empty" role="status"><RefreshCw className="spin" size={20} aria-hidden="true" /><div><b>Reading shared loops.</b><p>Grants appear as soon as they load.</p></div></div>
        ) : error ? (
          <div className="sharing-empty" role="alert"><UsersRound size={20} aria-hidden="true" /><div><b>Shared loops unavailable.</b><p>{error}</p></div><Button variant="secondary" onClick={() => setRefreshKey((value) => value + 1)}>Try again</Button></div>
        ) : loops.length ? (
          <div className="sharing-shared-list">
            {loops.map((loop) => {
              const rule = ruleFor(loop);
              return (
                <Link to={`/app/loops/${loop.id}`} key={loop.id}>
                  <ProviderMark provider={loop.discovery?.source.provider} size={26} />
                  <span><b>{loop.name}</b><small>{sharedByLine({ ownerName: loop.owner?.name, originName: loop.visibility.originOrganizationName, rule })}{loop.visibility.ruleId && !rule ? " · by rule" : ""}</small></span>
                  {(rule || loop.visibility.ruleId) && <em className="sharing-rule-chip">rule</em>}
                  <ArrowRight size={14} aria-hidden="true" />
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="sharing-empty"><UsersRound size={25} aria-hidden="true" /><div><b>Nothing shared into this group yet.</b><p>Members share loops from their private Sharing page, one at a time, in bulk, or with a standing rule.</p></div></div>
        )}
      </section>
    </div>
  );
}
